/**
 * MixerPanel Component
 * A container that renders multiple MixerChannelStrip components
 * 
 * Features:
 * - Horizontal scrollable mixer view
 * - Master channel strip
 * - Dynamic track rendering
 * - Collapsible panel
 */
import { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Volume2, Settings2 } from 'lucide-react';
import { MixerChannelStrip } from './MixerChannelStrip';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';

/**
 * Master Channel Strip
 * Special channel strip for master output control
 */
const MasterChannelStrip = ({ 
  volume = 0.8, 
  onVolumeChange,
  className 
}) => {
  return (
    <div 
      className={cn(
        "flex flex-col items-center p-3 rounded-lg border",
        "bg-gradient-to-b from-zinc-800 to-zinc-900",
        "border-amber-500/30",
        className
      )}
      data-testid="mixer-master-channel"
    >
      {/* Master label */}
      <div className="w-full text-center mb-2">
        <span className="text-[10px] font-bold uppercase text-amber-500">Master</span>
      </div>
      
      {/* Volume fader */}
      <div className="relative w-8 h-32 bg-zinc-800 rounded-sm mb-2">
        <div className="absolute inset-x-2 inset-y-1 bg-zinc-900 rounded-sm overflow-hidden">
          <motion.div 
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-amber-600 to-amber-400"
            style={{ height: `${volume * 100}%` }}
            animate={{ height: `${volume * 100}%` }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          />
        </div>
        
        {/* Fader handle */}
        <motion.div 
          className="absolute left-0 right-0 h-4 bg-amber-200 rounded-sm shadow-lg border border-amber-300 flex items-center justify-center"
          style={{ bottom: `calc(${volume * 100}% - 8px)` }}
          animate={{ bottom: `calc(${volume * 100}% - 8px)` }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <div className="w-4 h-0.5 bg-amber-600 rounded" />
        </motion.div>
        
        {/* dB markers */}
        <div className="absolute -right-6 inset-y-0 flex flex-col justify-between text-[8px] text-zinc-500 py-1">
          <span>+6</span>
          <span>0</span>
          <span>-6</span>
          <span>-12</span>
          <span>-∞</span>
        </div>
        
        {/* Click area for fader control */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onMouseDown={(e) => {
            e.preventDefault();
            const rect = e.currentTarget.getBoundingClientRect();
            const handleMove = (moveEvent) => {
              const y = moveEvent.clientY - rect.top;
              const newValue = Math.max(0, Math.min(1, 1 - (y / rect.height)));
              onVolumeChange?.(newValue);
            };
            const handleUp = () => {
              document.removeEventListener('mousemove', handleMove);
              document.removeEventListener('mouseup', handleUp);
            };
            document.addEventListener('mousemove', handleMove);
            document.addEventListener('mouseup', handleUp);
            handleMove(e);
          }}
        />
      </div>
      
      {/* dB value */}
      <div className="text-[10px] font-mono text-amber-400 mb-2">
        {volume === 0 ? '-∞' : `${(20 * Math.log10(volume)).toFixed(1)}`} dB
      </div>
      
      {/* Master icon */}
      <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
        <Volume2 className="w-4 h-4 text-amber-500" />
      </div>
    </div>
  );
};

/**
 * Main MixerPanel Component
 */
export const MixerPanel = ({
  tracks = [],
  masterVolume = 0.8,
  selectedTrackId = null,
  onTrackVolumeChange,
  onTrackPanChange,
  onTrackMuteToggle,
  onTrackSoloToggle,
  onTrackSelect,
  onTrackNameChange,
  onRemoveTrack, // NEW: Delete track handler
  onMasterVolumeChange,
  // Effect chain handlers
  onAddEffect,
  onRemoveEffect,
  onToggleEffect,
  onReorderEffects,
  onEffectParamChange,
  // Plugin chain handlers
  onAddPlugin,
  onRemovePlugin,
  onTogglePluginBypass,
  onReorderPlugins,
  onPluginParamChange,
  onOpenPluginBrowser,
  onOpenTrackPresets, // NEW: Open track preset selector
  // Guitar effects handlers
  onGuitarEffectsChange,
  expandedGuitarEffectsTrack,
  onToggleGuitarEffectsExpand,
  userId = null, // For preset management
  peakLevels = {},
  isOpen = true,
  onToggle,
  className,
}) => {
  // Calculate if any track is soloed
  const hasSolo = useMemo(() => tracks.some(t => t.solo), [tracks]);
  
  return (
    <motion.div
      className={cn(
        "bg-zinc-900/95 backdrop-blur-md border-t border-zinc-700",
        className
      )}
      initial={false}
      animate={{ height: isOpen ? 'auto' : 48 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      data-testid="mixer-panel"
    >
      {/* Header */}
      <div 
        className="h-12 px-4 flex items-center justify-between cursor-pointer border-b border-zinc-800"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-cyan-500" />
          <span className="text-sm font-medium text-zinc-300">Mixer</span>
          <span className="text-xs text-zinc-500">({tracks.length} tracks)</span>
        </div>
        
        <Button variant="ghost" size="icon" className="w-6 h-6">
          {isOpen ? (
            <ChevronDown className="w-4 h-4 text-zinc-400" />
          ) : (
            <ChevronUp className="w-4 h-4 text-zinc-400" />
          )}
        </Button>
      </div>
      
      {/* Mixer content */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="p-4 overflow-x-auto"
          >
            <div className="flex gap-2 min-w-max">
              {/* Track channel strips */}
              {tracks.map((track, index) => (
                <MixerChannelStrip
                  key={track.id}
                  track={track}
                  index={index}
                  isSelected={track.id === selectedTrackId}
                  onVolumeChange={(value) => onTrackVolumeChange?.(track.id, value)}
                  onPanChange={(value) => onTrackPanChange?.(track.id, value)}
                  onMuteToggle={() => onTrackMuteToggle?.(track.id)}
                  onSoloToggle={() => onTrackSoloToggle?.(track.id)}
                  onSelect={() => onTrackSelect?.(track.id)}
                  onNameChange={(name) => onTrackNameChange?.(track.id, name)}
                  onRemoveTrack={() => onRemoveTrack?.(track.id)}
                  canRemoveTrack={tracks.length > 1}
                  peakLevel={peakLevels[track.id] || 0}
                  showMeter={true}
                  onAddEffect={onAddEffect}
                  onRemoveEffect={onRemoveEffect}
                  onToggleEffect={onToggleEffect}
                  onReorderEffects={onReorderEffects}
                  onEffectParamChange={onEffectParamChange}
                  onAddPlugin={onAddPlugin}
                  onRemovePlugin={onRemovePlugin}
                  onTogglePluginBypass={onTogglePluginBypass}
                  onReorderPlugins={onReorderPlugins}
                  onPluginParamChange={onPluginParamChange}
                  onOpenPluginBrowser={onOpenPluginBrowser}
                  onOpenTrackPresets={onOpenTrackPresets}
                  onGuitarEffectsChange={onGuitarEffectsChange}
                  guitarEffectsExpanded={expandedGuitarEffectsTrack === track.id}
                  onToggleGuitarEffectsExpand={() => onToggleGuitarEffectsExpand?.(track.id)}
                  userId={userId}
                />
              ))}
              
              {/* Spacer before master */}
              {tracks.length > 0 && (
                <div className="w-px bg-zinc-700 mx-2" />
              )}
              
              {/* Master channel */}
              <MasterChannelStrip
                volume={masterVolume}
                onVolumeChange={onMasterVolumeChange}
              />
            </div>
            
            {/* Empty state */}
            {tracks.length === 0 && (
              <div className="flex items-center justify-center py-8 text-zinc-500 text-sm">
                No tracks to display. Add a track to see mixer controls.
              </div>
            )}
            
            {/* Solo indicator */}
            {hasSolo && (
              <div className="mt-3 px-2 py-1 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-500 inline-flex items-center gap-1">
                <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                Solo active - only soloed tracks will play
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default MixerPanel;
